import React, { useMemo } from "react";
import { DateTime } from "luxon";
import i18n from "app/i18n";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import { formatMonthDay } from "app/utils/luxonFormats";

const EVENT_PATTERN = [
  [2, 0, 3, 0, 2, 0, 0],
  [0, 1, 0, 2, 0, 1, 0],
  [1, 2, 0, 0, 2, 0, 1],
  [0, 0, 1, 2, 0, 0, 0],
  [0, 1, 0, 0, 1, 0, 0],
];

interface MonthSkeletonProps {
  initialDate: DateTime;
  firstDay: number;
  dateFormat: "us" | "eu";
  timeZone: string;
}

export default function MonthSkeleton({
  initialDate,
  firstDay,
  dateFormat,
  timeZone,
}: MonthSkeletonProps) {
  const { dayNames, weeks } = useMemo(() => {
    // Work in the calendar's time zone so the skeleton grid matches the real
    // one it precedes (CN-2586).
    const dt = (initialDate || DateTime.now()).setZone(timeZone);
    const monthStart = dt.startOf("month");
    const monthEnd = dt.endOf("month");
    const todayDate = DateTime.now().setZone(timeZone).startOf("day");

    const names: string[] = [];
    for (let i = 0; i < 7; i++) {
      const dow = (firstDay + i) % 7;
      const weekday = (dow === 0 ? 7 : dow) as 1 | 2 | 3 | 4 | 5 | 6 | 7;
      const d = DateTime.fromObject({ weekday });
      names.push(
        d.toLocaleString({ weekday: "short" }, { locale: i18n.language }),
      );
    }

    const gridStart = monthStart.startOf("week", { useLocaleWeeks: false });
    const adjustedStart =
      gridStart.weekday === monthStart.weekday && gridStart >= monthStart
        ? gridStart.minus({ weeks: 1 })
        : gridStart;

    let start = adjustedStart;
    if (firstDay === 0) {
      start = monthStart.startOf("week").minus({ days: 1 });
      if (start.month === monthStart.month && start.day > 1) {
        start = start.minus({ weeks: 1 });
      }
    } else {
      start = monthStart.startOf("week");
      if (start > monthStart) {
        start = start.minus({ weeks: 1 });
      }
    }

    const rows: Array<
      Array<{
        day: number;
        isOther: boolean;
        isToday: boolean;
        dow: number;
        label: string;
      }>
    > = [];
    let current = start;
    for (let r = 0; r < 5; r++) {
      const week: (typeof rows)[0] = [];
      for (let c = 0; c < 7; c++) {
        const isOther = current < monthStart || current > monthEnd;
        const isToday = current.hasSame(todayDate, "day");
        const day = current.day;
        const dow = current.weekday % 7;
        let label = String(day);
        if (day === 1) {
          label = formatMonthDay(current, dateFormat);
        }
        week.push({ day, isOther, isToday, dow, label });
        current = current.plus({ days: 1 });
      }
      rows.push(week);
    }

    return { dayNames: names, weeks: rows };
  }, [initialDate, firstDay, dateFormat, timeZone]);

  return (
    <div className={styles.skeleton}>
      <div className={styles.skeleton_header}>
        {dayNames.map((name, i) => (
          <div key={i} className={styles.skeleton_header_cell}>
            {name}
          </div>
        ))}
      </div>
      <div className={styles.skeleton_grid}>
        {weeks.map((week, r) => (
          <div key={r} className={styles.skeleton_row}>
            {week.map((cell, c) => {
              const isWeekend = cell.dow === 0 || cell.dow === 6;
              const cellClass = [
                styles.skeleton_cell,
                isWeekend ? styles.skeleton_cell_weekend : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div key={c} className={cellClass}>
                  <div
                    className={[
                      styles.skeleton_day_top,
                      cell.isToday ? styles.skeleton_day_top_today : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span
                      className={[
                        styles.skeleton_day_number,
                        cell.isOther ? styles.skeleton_day_other : "",
                        cell.isToday ? styles.skeleton_day_today : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {cell.label}
                    </span>
                  </div>
                  {isWeekend ? (
                    <div className={styles.skeleton_unavailable} />
                  ) : (
                    <div
                      className={cls(styles.skeleton_events, {
                        [styles.skeleton_events_margin]: cell.isToday,
                      })}
                    >
                      {Array.from(
                        { length: EVENT_PATTERN[r]?.[c] || 0 },
                        (_, i) => (
                          <div key={i} className={styles.skeleton_event}>
                            <div className={styles.skeleton_event_bar} />
                            <div className={styles.skeleton_event_text} />
                            <div className={styles.skeleton_event_dot} />
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
